import React, { useCallback, useEffect, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useDeepCompareCallback, useDeepCompareMemo } from 'use-deep-compare';
import axios, { AxiosResponse } from 'axios';

import { Button } from '@trqk/components';
import { queryStrings } from '@trqk/resolvers';
import { getGapChannelStats, getWebpackEnvVar, locale } from '@trqk/utils';
import { useStateWithCallback } from '@trqk/hooks';

import { StepWrapper, DownloadAsButton } from '../..';
import { ConfirmChannelsTable } from './components';

import { useToast, useTokenModal } from '../../../../../../hooks';

const { GET_ACTIVE_LOG_POLLS } = queryStrings;

const UPDATE_TRQK_GAP_SELECTIONS = `
    mutation updateTrqkGapSelections($gap_analysis_id: String!, $selections: [trqkGapSelection!]!) {
        updateTrqkGapSelections(gap_analysis_id: $gap_analysis_id, selections: $selections){
            channels_included {
                audios_detected_raw
                channel_id
                channel_name_detection
                channel_name_statement
                estimated_loss_max
                estimated_loss_min
                include
                performances_matched
                performances_raw
            }
            date_created
            description
            entity_id
            gap_analysis_id
            performance_period
            performance_period_end
            performance_period_start
            statement_id_international
            statement_id_standard
            user_id
        }
    }
`;

/**
 * Renders a table of the channels for a specific report period. Allows the user to select channels
 * to include in the Gap Analysis report. To select a channel, the user must select at least one of
 * its underlying audio files - this part is handled in the ConfirmWorks step.
 * @param props - {@link GapAnalysisStepProps}
 */
export const ConfirmChannels: GapAnalysisStepComponent = props => {
    const { context, remove, send } = props;
    const { channels, channelsUpdated, edits, trqkGap } = context;
    const trqkDirectoryUrl = getWebpackEnvVar('web', 'TRQK_DIRECTORY_URL');
    const { getTokenAsync } = useTokenModal();
    const { add: addToast } = useToast();
    const [refreshTime, setRefreshTime] = useState(0);
    const gapAnalysisId = trqkGap?.gap_analysis_id ?? null;
    const updateRefreshTime = useCallback(() => {
        setRefreshTime(Date.now());
    }, []);

    // After `gapChannelStats` has been updated, inform the table that both `gapChannels` and
    // `gapChannelStats` have been updated by setting a new value in `refreshTime`
    const [gapChannelStats, setGapChannelStats] =
        useStateWithCallback<GapChannelStats>(
            {
                detectionsMax: 0,
                performancesMax: 0,
            },
            updateRefreshTime,
        );

    // Listen for changes in `context.channelsUpdated` and use them to update the state of
    // `gapChannels` and `gapChannelStats`.
    useEffect(() => {
        const nextGapChannelStats = getGapChannelStats(channels);

        setGapChannelStats(nextGapChannelStats);
    }, [channelsUpdated]);

    const { cache } = useApolloClient();

    // By the time we get to this step, we can assume there are no more active detection uploads,
    // so we update the cache accordingly. When there are also no statement uploads, this means the
    // LogDataProvider will use the long polling interval.
    useEffect(() => {
        cache.writeQuery({
            query: GET_ACTIVE_LOG_POLLS,
            data: {
                uiState: {
                    __typename: 'UIState',
                    activeLogPolls: {
                        __typename: 'ActiveLogPolls',
                        uploadDetections: false,
                    },
                },
            },
        });
    }, []);

    const hasIncludedChannel = channels.some(
        ({ include }: TrqkGapChannel) => include,
    );
    const hasCheckedChannel = Object.values(edits).some(
        ({ checked }: GapAnalysisEdits) => checked,
    );
    const hasSelectedChannel = hasIncludedChannel || hasCheckedChannel;

    useEffect(() => {
        send({
            type: 'UPDATE_STEP',
            value: {
                stepId: 'confirmChannels',
                update: {
                    disableNextStep: !hasSelectedChannel,
                },
            },
        });
    }, [hasSelectedChannel]);

    const selections = useDeepCompareMemo(
        () =>
            Object.entries(edits).reduce(
                (
                    acc: TrqkGapSelection[],
                    [channel_id, channelEdits],
                ): TrqkGapSelection[] => {
                    if (channelEdits.checked) {
                        // The field on a TrqkGapSelection is called `gap_channel_id`, but it really
                        // refers to a `channel.channel_id`
                        acc.push({
                            gap_channel_id: channel_id,
                        });
                    }

                    return acc;
                },
                [],
            ),
        [edits],
    );

    const hasSelections = selections.length > 0;

    useEffect(() => {
        send({
            type: 'UPDATE_STEP',
            value: {
                stepId: 'confirmChannels',
                update: {
                    disableNextStep: !hasSelections,
                },
            },
        });
    }, [hasSelections]);

    const [saving, setSaving] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const saveChannelSelection = useDeepCompareCallback(
        async ({
            toastOnSuccess,
            toastOnError,
            setLoading,
        }: SaveChannelSelectionInput) => {
            if (selections.length < 1 || gapAnalysisId === null) {
                return false;
            }

            const token = await getTokenAsync();

            setLoading(true);

            return axios({
                method: 'post',
                url: trqkDirectoryUrl,
                headers: {
                    Authorization: token,
                },
                data: {
                    query: UPDATE_TRQK_GAP_SELECTIONS,
                    variables: {
                        gap_analysis_id: gapAnalysisId,
                        selections,
                    },
                },
            })
                .then(
                    (
                        response: AxiosResponse<{
                            data: {
                                updateTrqkGapSelections: TrqkGap;
                            };
                        }>,
                    ) => {
                        setLoading(false);

                        const nextTrqkGap =
                            response?.data?.data?.updateTrqkGapSelections ??
                            null;

                        if (nextTrqkGap) {
                            if (toastOnSuccess) {
                                addToast({
                                    kind: 'info-banner',
                                    content:
                                        locale.enUS.MANAGE_GAP_ANALYSIS.STEPS
                                            .CONFIRM_CHANNELS
                                            .CHANGES_HAVE_BEEN_SAVED,
                                });
                            }

                            return true;
                        }

                        if (toastOnError) {
                            addToast({
                                kind: 'warning-banner',
                                content:
                                    locale.enUS.API_ERRORS
                                        .updateTrqkGapSelections,
                            });
                        }

                        return false;
                    },
                )
                .catch((): boolean => false);
        },
        [gapAnalysisId, selections],
    );

    return (
        <StepWrapper
            // extraButton={}
            context={context}
            extraButtons={
                <>
                    <Button
                        addClasses="respond-to-mouse"
                        color="secondary-light-bg"
                        disabled={selections.length < 1}
                        loading={saving}
                        width={180}
                        handleClick={() => {
                            saveChannelSelection({
                                toastOnSuccess: true,
                                toastOnError: true,
                                setLoading: setSaving,
                            });
                        }}
                    >
                        <p className="text-center">
                            {
                                locale.enUS.MANAGE_GAP_ANALYSIS.STEPS
                                    .CONFIRM_CHANNELS.SAVE_CHANGES
                            }
                        </p>
                    </Button>
                    <DownloadAsButton
                        gapAnalysisId={gapAnalysisId}
                        beforeDownload={async () =>
                            saveChannelSelection({
                                toastOnSuccess: false,
                                toastOnError: true,
                                setLoading: setDownloading,
                            })
                        }
                        savingChannelSelection={downloading}
                    />
                </>
            }
            remove={remove}
            send={send}
            helpLinkHref="https://www.trqk.io/faq#gap-analysis-channels"
        >
            <ConfirmChannelsTable
                context={context}
                refreshTime={refreshTime}
                gapChannels={channels}
                gapChannelStats={gapChannelStats}
                send={send}
            />
        </StepWrapper>
    );
};
